import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { getLocaleId } from 'utils/getLocaleId'
import useNotification from 'utils/notification'
import { ProductViewBySlugQuery } from 'api/operations'
import { useProductViewBySlugQuery, useProductVisitMutation } from 'api/hooks'

export const ProductContext = createContext(null)

export const useProduct = (): ProductViewBySlugQuery['productBySlug'] => {
  return useContext(ProductContext)
}

export const useProductData = slug => {
  const localeId = getLocaleId()
  const enLocaleId = getLocaleId('en')
  const notify = useNotification()

  const queryResult = useProductViewBySlugQuery({
    variables: {
      slug,
      localeId,
      enLocaleId,
      withEnLocaleId: localeId !== enLocaleId,
    },
    fetchPolicy: 'no-cache',
    onError(error) {
      notify(error.message)
    },
  })

  const data = useMemo(() => {
    const data = queryResult?.data?.productBySlug
    if (data?.wine) {
      if (!data.wine.techSheet) {
        data.wine.techSheet = data.wine.techSheetEn
      }
      data.wine.techSheetEn = undefined
    }
    return data
  }, [queryResult])

  return {
    ...queryResult,
    data,
  }
}

const PRODUCER_ENTITY = 'producer'
export const PRODUCT_ENTITY = 'product'
const BRAND_ENTITY = 'brand'
export const WINE_ENTITY = 'wine'

export const useAcceptabilityData = ({ id, data, entity }) => {
  const [acceptable, setAcceptable] = useState(true)
  const [canOverride, setCanOverride] = useState(false)
  const [unaccaptableEntity, setUnaccaptableEntity] = useState('')
  const [unaccaptableEntityID, setUnaccaptableEntityID] = useState('')
  const [entityRoute, setEntityRoute] = useState('')

  const productAcceptability =
    entity === PRODUCT_ENTITY ? data?.acceptability ?? false : false

  const canOverrideProductAcceptability =
    entity === PRODUCT_ENTITY ? data?.canOverrideAcceptability ?? false : false

  const wineAcceptability =
    entity === PRODUCT_ENTITY
      ? data?.wine?.acceptability ?? false
      : data?.acceptability ?? false

  const canOverrideWineAcceptability =
    entity === PRODUCT_ENTITY
      ? data?.wine?.canOverrideAcceptability ?? false
      : data?.canOverrideAcceptability ?? false

  const brandAcceptability =
    entity === PRODUCT_ENTITY
      ? data?.wine?.brand?.acceptability ?? true
      : data?.brand?.acceptability ?? true

  const canOverrideBrandAcceptability =
    entity === PRODUCT_ENTITY
      ? data?.wine?.brand?.canOverrideAcceptability ?? true
      : data?.brand?.canOverrideAcceptability ?? true

  const tradeNameAcceptability =
    entity === PRODUCT_ENTITY
      ? data?.wine?.tradeName?.acceptability ?? true
      : data?.tradeName?.acceptability ?? true

  const canOverrideTradeNameAcceptability =
    entity === PRODUCT_ENTITY
      ? data?.wine?.tradeName?.canOverrideAcceptability ?? true
      : data?.tradeName?.canOverrideAcceptability ?? true

  const wineId =
    entity === PRODUCT_ENTITY ? data?.wine?.id ?? '' : data?.id ?? ''

  const brandId =
    entity === PRODUCT_ENTITY
      ? data?.wine?.brand?.id ?? ''
      : data?.brand?.id ?? ''

  const tradeNameId =
    entity === PRODUCT_ENTITY
      ? data?.wine?.tradeName?.id ?? ''
      : data?.tradeName?.id ?? ''

  useEffect(() => {
    if (data) {
      if (entity === PRODUCT_ENTITY) {
        if (data.id) {
          if (!productAcceptability) {
            setAcceptable(false)
            setCanOverride(canOverrideProductAcceptability)
            setUnaccaptableEntity(PRODUCT_ENTITY)
            setUnaccaptableEntityID(id)
            setEntityRoute(`wine/${wineId}`)
          } else if (!wineAcceptability) {
            setAcceptable(false)
            setCanOverride(canOverrideWineAcceptability)
            setUnaccaptableEntity(WINE_ENTITY)
            setUnaccaptableEntityID(wineId)
            setEntityRoute(`wine/${wineId}`)
          } else if (!brandAcceptability) {
            setAcceptable(false)
            setCanOverride(canOverrideBrandAcceptability)
            setUnaccaptableEntity(BRAND_ENTITY)
            setUnaccaptableEntityID(brandId)
            setEntityRoute('brand')
          } else if (!tradeNameAcceptability) {
            setAcceptable(false)
            setCanOverride(canOverrideTradeNameAcceptability)
            setUnaccaptableEntity(PRODUCER_ENTITY)
            setUnaccaptableEntityID(tradeNameId)
            setEntityRoute('producer')
          } else {
            setAcceptable(true)
          }
        }
      } else if (entity === WINE_ENTITY) {
        if (data.id) {
          if (!wineAcceptability) {
            setAcceptable(false)
            setCanOverride(canOverrideWineAcceptability)
            setUnaccaptableEntity(WINE_ENTITY)
            setUnaccaptableEntityID(id)
            setEntityRoute(`wine/${wineId}`)
          } else if (!brandAcceptability) {
            setAcceptable(false)
            setCanOverride(canOverrideBrandAcceptability)
            setUnaccaptableEntity(BRAND_ENTITY)
            setUnaccaptableEntityID(brandId)
            setEntityRoute('brand')
          } else if (!tradeNameAcceptability) {
            setAcceptable(false)
            setCanOverride(canOverrideTradeNameAcceptability)
            setUnaccaptableEntity(PRODUCER_ENTITY)
            setUnaccaptableEntityID(tradeNameId)
            setEntityRoute('producer')
          } else {
            setAcceptable(true)
          }
        }
      }
    }
  }, [data])

  return [
    acceptable,
    canOverride,
    unaccaptableEntity,
    unaccaptableEntityID,
    entityRoute,
  ]
}

export const useProductVisit = (productId: string) => {
  const [visit] = useProductVisitMutation()

  useEffect(() => {
    if (productId) {
      visit({ variables: { id: productId } })
    }
  }, [productId])
}
